// Someone edited a product in Autotask, outside Omni. Omni never applies or
// reverts that on its own: ops keep Omni's values (written back to Autotask) or
// take Autotask's (Omni updates itself), for the whole product at once.
import React from "react";
import { Icon } from "../../icons.js";
import { whenIn } from "../../format.js";
import { Callout, Sheet } from "../../ui.js";
import { useCatalog } from "./context.js";
import { atRef, type Product } from "./model.js";
import { DiffTable } from "./ProductSheet.js";

export function DiffSheet({ p }: { p: Product }): React.ReactElement {
  const { autotask, open, close, problem, busy } = useCatalog();
  const active = p.autotaskSync.differences.some((d) => d.field === "active");
  return (
    <Sheet
      title="Changed in Autotask"
      onClose={close}
      foot={
        <>
          <button className="btn" type="button" onClick={() => open(p.id)}>
            Back
          </button>
          <button className="btn" type="button" disabled={busy} data-testid="take-autotask" onClick={() => void autotask(p, "take_autotask")}>
            Take Autotask{"\u2019"}s
          </button>
          <button className="btn primary" type="button" disabled={busy} data-testid="keep-omni" onClick={() => void autotask(p, "keep_omni")}>
            Keep Omni{"\u2019"}s
          </button>
        </>
      }
    >
      <div className="omni-cat" data-testid="diff-sheet" data-product={p.id}>
        {problem ? (
          <Callout kind="stop" icon="triangle-alert">
            {problem}
          </Callout>
        ) : null}
        <div className="sec">
          <b>{p.name}</b>
          <span className="code block">
            {atRef(p)}
            {p.autotaskSync.checkedAt ? ` \u00b7 seen by the check ${whenIn(p.autotaskSync.checkedAt)}` : ""}
          </span>
          <p className="hint">
            Someone edited this in Autotask, outside Omni. Omni owns these fields, so it never applies or reverts the change on its own. Choose one for the whole product.
          </p>
        </div>
        <div className="sec">
          <DiffTable p={p} testId="diff-table" />
        </div>
        <div className="sec">
          <ul className="reasons">
            <li className="ok">
              <Icon name="arrow-right-left" size={14} />
              <span>
                <b>Keep Omni{"\u2019"}s</b> writes Omni{"\u2019"}s values back to Autotask, then reads it back.
              </span>
            </li>
            <li className="ok">
              <Icon name="check" size={14} />
              <span>
                <b>Take Autotask{"\u2019"}s</b> updates Omni to match Autotask.{" "}
                {active ? "Taking Inactive stops dealers quoting it." : "Quotes from now on use the new values; quotes already sent keep theirs."}
              </span>
            </li>
          </ul>
        </div>
      </div>
    </Sheet>
  );
}
